import { useCallback, useRef } from 'react';

import { useFeature } from '~/modules/root';
import { shouldOfferPasskeyEnrolment } from './passkey-device-hint';
import { logPasskeySuppressed } from './passkey-diagnostics';
import {
    mintEnrolmentOptions,
    type PasskeyEnrolmentMint,
} from './passkey-enrol-mint';
import { detectPasskeySupport } from './passkey-support';

const declineOffer = (reason: string): null => {
    logPasskeySuppressed('Secure Sign-In offer', reason);
    return null;
};

/**
 * Why the server last refused to mint, for the rest of this page load.
 *
 * The reasons it refuses are properties of the account or of the service — MFA
 * on the account, staff policy, a session it cannot see, the backend being
 * down — not of the moment, so a second ask on the same page would spend
 * another round trip to be told the same thing. Latched rather than retried,
 * because the cost of being wrong is only a missed offer, while the cost of
 * asking anyway is a step that opens and withdraws.
 */
let refusedReason: string | undefined;

/**
 * Decides whether the post-auth Secure Sign-In ask should show on this device,
 * and mints the challenge it would need.
 *
 * Answers with the mint or with nothing, so a caller that gets an offer can
 * render the ask outright. The alternative — deciding on the local checks and
 * letting the step discover the server's answer for itself — is how a step
 * came to open on a device that could never complete it and then withdraw,
 * leaving an empty surface behind on any host with no dialog to close.
 *
 * Asynchronous because platform-authenticator detection is: a gate can reach
 * its decision in the same tick it mounts, and answering "unsupported" while
 * the question is still open is how the ask went missing on devices that do
 * support it. The local checks run first, so a device that was never going to
 * be asked costs no round trip. Only the cadence check is latched, because
 * {@link shouldOfferPasskeyEnrolment} advances the skip counter when it
 * suppresses — it must run at most once per auth journey. `reset()` clears the
 * latch for a fresh journey.
 */
export const usePasskeyEnrolOffer = () => {
    // Passkeys ship under the same flag as the auth modal revamp.
    const passkeyFlagEnabled = useFeature('auth_modal_2026');
    const cadenceRef = useRef<boolean | undefined>(undefined);

    const decideOffer = useCallback(
        async ({
            memberID,
            isNewAccount = false,
        }: {
            memberID?: number;
            isNewAccount?: boolean;
        } = {}): Promise<PasskeyEnrolmentMint | null> => {
            if (!passkeyFlagEnabled) {
                return declineOffer('auth_modal_2026 is off');
            }

            if (!(await detectPasskeySupport())) {
                return declineOffer('no platform authenticator on this device');
            }

            if (cadenceRef.current === undefined) {
                cadenceRef.current = shouldOfferPasskeyEnrolment({
                    memberID,
                    isNewAccount,
                });
            }
            if (!cadenceRef.current) {
                return declineOffer(
                    'this member already enrolled on this device, or skipped ' +
                        'too recently (clear localStorage "ap-passkey-hint" ' +
                        'to reset)',
                );
            }

            if (refusedReason !== undefined) {
                return declineOffer(
                    `the server already refused to mint on this page load: ${refusedReason}`,
                );
            }

            try {
                return await mintEnrolmentOptions();
            } catch (err) {
                // Not marked as a user skip — the cadence counts declines, not
                // server refusals.
                refusedReason =
                    err instanceof Error && err.message
                        ? err.message
                        : 'the server would not issue a challenge';

                return declineOffer(refusedReason);
            }
        },
        [passkeyFlagEnabled],
    );

    const reset = useCallback(() => {
        cadenceRef.current = undefined;
    }, []);

    return { decideOffer, reset };
};
